/**
 * EntityManager — secret-admin quick-add for clubs, venues, and tournaments.
 *  - No owner-signup flow needed; the secret admin can create entities
 *    directly. Useful for seeding new cities or onboarding partners over a
 *    phone call.
 *  - Each created entity is stamped with `created_by: "secret_admin"` so
 *    organizers can be invited later via the regular owner flow.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Building2, MapPin, Trophy } from 'lucide-react';
import { toast } from 'sonner';

function ClubForm({ apiCall }) {
  const [f, setF] = useState({ name: '', city: '', location: '', description: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const r = await apiCall('/api/admin-secret/clubs', { method: 'POST', body: JSON.stringify(f) });
      toast.success(`Club "${r.name}" created`);
      setF({ name: '', city: '', location: '', description: '' });
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Name *</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="ent-club-name" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">City</Label><Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} data-testid="ent-club-city" /></div>
        <div><Label className="text-xs">Location</Label><Input value={f.location} onChange={e => setF({ ...f, location: e.target.value })} data-testid="ent-club-location" /></div>
      </div>
      <div><Label className="text-xs">Description</Label><Input value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="ent-club-desc" /></div>
      <Button onClick={submit} disabled={busy} data-testid="ent-club-submit">{busy ? 'Creating…' : 'Create Club'}</Button>
    </div>
  );
}

function VenueForm({ apiCall }) {
  const [f, setF] = useState({ name: '', address: '', city: '', courts: 1 });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const r = await apiCall('/api/admin-secret/venues', { method: 'POST', body: JSON.stringify(f) });
      toast.success(`Venue "${r.name}" created`);
      setF({ name: '', address: '', city: '', courts: 1 });
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Name *</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="ent-venue-name" /></div>
      <div><Label className="text-xs">Address</Label><Input value={f.address} onChange={e => setF({ ...f, address: e.target.value })} data-testid="ent-venue-address" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">City</Label><Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} data-testid="ent-venue-city" /></div>
        <div><Label className="text-xs">Courts</Label><Input type="number" min={1} value={f.courts} onChange={e => setF({ ...f, courts: parseInt(e.target.value) || 1 })} data-testid="ent-venue-courts" /></div>
      </div>
      <Button onClick={submit} disabled={busy} data-testid="ent-venue-submit">{busy ? 'Creating…' : 'Create Venue'}</Button>
    </div>
  );
}

function TournamentForm({ apiCall }) {
  const [f, setF] = useState({
    name: '', city: '', venue: '', match_type: 'singles',
    format: 'single_elimination', best_of: 1, points_per_game: 21,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const r = await apiCall('/api/admin-secret/tournaments', { method: 'POST', body: JSON.stringify(f) });
      toast.success(`Tournament "${r.name}" created`);
      setF({ name: '', city: '', venue: '', match_type: 'singles', format: 'single_elimination', best_of: 1, points_per_game: 21 });
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Name *</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="ent-tour-name" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">City</Label><Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} data-testid="ent-tour-city" /></div>
        <div><Label className="text-xs">Venue</Label><Input value={f.venue} onChange={e => setF({ ...f, venue: e.target.value })} data-testid="ent-tour-venue" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Match Type</Label>
          <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={f.match_type} onChange={(e) => setF({ ...f, match_type: e.target.value })}
                  data-testid="ent-tour-type">
            <option value="singles">Singles</option>
            <option value="doubles">Doubles</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Format</Label>
          <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={f.format} onChange={(e) => setF({ ...f, format: e.target.value })}
                  data-testid="ent-tour-format">
            <option value="single_elimination">Single Elimination</option>
            <option value="round_robin">Round Robin</option>
            <option value="group_playoff">Group + Playoff</option>
            <option value="super_8">Super 8</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Best of</Label><Input type="number" min={1} max={5} value={f.best_of} onChange={e => setF({ ...f, best_of: parseInt(e.target.value) || 1 })} data-testid="ent-tour-bo" /></div>
        <div><Label className="text-xs">Points / game</Label><Input type="number" min={6} max={31} value={f.points_per_game} onChange={e => setF({ ...f, points_per_game: parseInt(e.target.value) || 21 })} data-testid="ent-tour-ppg" /></div>
      </div>
      <Button onClick={submit} disabled={busy} data-testid="ent-tour-submit">{busy ? 'Creating…' : 'Create Tournament'}</Button>
    </div>
  );
}

export default function EntityManager({ apiCall }) {
  return (
    <section data-testid="entity-manager-section">
      <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-bold">Quick-Add Entities</h2>
      <Card>
        <CardContent className="p-4">
          <Tabs defaultValue="club">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="club" data-testid="ent-tab-club"><Building2 className="h-3.5 w-3.5 mr-1" /> Club</TabsTrigger>
              <TabsTrigger value="venue" data-testid="ent-tab-venue"><MapPin className="h-3.5 w-3.5 mr-1" /> Venue</TabsTrigger>
              <TabsTrigger value="tournament" data-testid="ent-tab-tournament"><Trophy className="h-3.5 w-3.5 mr-1" /> Tournament</TabsTrigger>
            </TabsList>
            <TabsContent value="club" className="pt-4"><ClubForm apiCall={apiCall} /></TabsContent>
            <TabsContent value="venue" className="pt-4"><VenueForm apiCall={apiCall} /></TabsContent>
            <TabsContent value="tournament" className="pt-4"><TournamentForm apiCall={apiCall} /></TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </section>
  );
}
